import { useState, useEffect } from 'react';
import { Dispatch, SetStateAction } from 'react';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { Modal, Button } from '@mui/material';
import { useCreateFaq, useUpdateFaq } from '../api/hooks/useFaq';
import { showSuccess, showError } from '../helpers/toast';
import type { Faq } from '../api/types/faq.types';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
    faq?: Faq | null; // FAQ to edit (null for create mode)
    onSuccess?: () => void; // Callback after successful create/update
}

export default function AddFaq({ open, onClose, setOpen, faq, onSuccess }: ModalProps) {
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState(false);

    const isEditMode = !!faq;

    // Reset form when modal opens/closes or FAQ changes
    useEffect(() => {
        if (open) {
            if (faq) {
                setQuestion(faq.question || "");
                setAnswer(faq.answer || "");
            } else {
                setQuestion("");
                setAnswer("");
            }
            setError(false);
        }
    }, [open, faq]);

    const { mutate: createFaq, isPending: isCreating } = useCreateFaq({
        onSuccess: (response) => {
            showSuccess(response.message || "FAQ created successfully");
            handleClose();
            onSuccess?.();
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to create FAQ");
        },
    });

    const { mutate: updateFaq, isPending: isUpdating } = useUpdateFaq({
        onSuccess: (response) => {
            showSuccess(response.message || "FAQ updated successfully");
            handleClose();
            onSuccess?.();
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to update FAQ");
        },
    });

    const handleClose = () => {
        setQuestion("");
        setAnswer("");
        setError(false);
        setOpen(false);
        onClose();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validation
        if (!question.trim()) {
            setError(true);
            showError("Question is required");
            return;
        }
        if (!answer.trim()) {
            setError(true);
            showError("Answer is required");
            return;
        }

        if (isEditMode && faq?._id) {
            // Update existing FAQ
            updateFaq({
                id: faq._id,
                data: { question: question.trim(), answer: answer.trim() },
            });
        } else {
            // Create new FAQ
            createFaq({
                question: question.trim(),
                answer: answer.trim(),
            });
        }
    };

    const isLoading = isCreating || isUpdating;

    return (
        <Modal
            className="modal addNotification_modal"
            open={open}
            onClose={handleClose}
        >
            <div className="modal-dialog">
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>{isEditMode ? "Edit FAQ" : "Add FAQ"}</h2>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                    <form className="form" onSubmit={handleSubmit}>
                        <div className="control_group">
                            <TextField
                                placeholder="Question"
                                fullWidth
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                hiddenLabel
                                disabled={isLoading}
                            />
                            {error && !question.trim() ? (
                                <h6 className="err_msg">Question is required</h6>
                            ) : null}
                        </div>
                        <div className="control_group">
                            <TextField
                                placeholder="Answer"
                                fullWidth
                                hiddenLabel
                                multiline
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                maxRows={5}
                                minRows={5}
                                disabled={isLoading}
                            />
                            {error && !answer.trim() ? (
                                <h6 className="err_msg">Answer is required</h6>
                            ) : null}
                        </div>
                        <div className="footer_btn_flex">
                            <Button
                                className="btn btn_grey"
                                onClick={handleClose}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="btn btn_primary"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Submitting..." : isEditMode ? "Update" : "Submit"}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
}
